// Common API utilities

import { PAGINATION } from '../../constants/apiConstants';

class ApiHelpers {
  // Build query string from object
  buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) return '';
    
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map(v => `${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`).join('&');
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .join('&');
    
    return queryString ? `?${queryString}` : '';
  }

  // Build pagination params
  buildPaginationParams(page = PAGINATION.DEFAULT_PAGE, limit = PAGINATION.DEFAULT_LIMIT) {
    return {
      page: Math.max(1, page),
      limit: Math.min(limit, PAGINATION.MAX_LIMIT),
    };
  }

  // Format response data
  formatResponse(response) {
    return {
      data: response.data?.data || response.data,
      message: response.data?.message || null,
      status: response.status,
      pagination: response.data?.pagination || null,
    };
  }

  // Check if response is successful
  isSuccessResponse(status) {
    return status >= 200 && status < 300;
  }

  // Debounce function for search/filter
  debounce(func, delay = 300) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  // Format price
  formatPrice(price, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(price);
  }

  // Format date
  formatDate(date, options = {}) {
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(new Date(date));
  }

  // Deep clone object
  deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  // Check if object is empty
  isEmpty(obj) {
    return Object.keys(obj).length === 0;
  }

  // Remove empty fields from object
  removeEmptyFields(obj) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  // Convert FormData to object
  formDataToObject(formData) {
    const obj = {};
    for (let [key, value] of formData.entries()) {
      obj[key] = value;
    }
    return obj;
  }

  // Convert object to FormData
  objectToFormData(obj) {
    const formData = new FormData();
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
    return formData;
  }
}

// Export singleton instance
const apiHelpers = new ApiHelpers();
export default apiHelpers;
